import { useState, useRef, useEffect } from "react";
import { ContentItem, ContentCardProps } from "./types";
import { ContentCard as ContentCardUI } from "@/components/ui/content-card";
import CardThumbnail from "./CardThumbnail";
import GridCardDeleteDialog from "./GridCardDeleteDialog";
import CardActionMenu from "./CardActionMenu";
import { Tag, X } from "lucide-react";
import { useContentTags } from "@/hooks/useContentTags";
import AddTagButton from "@/components/common/AddTagButton";
import { useDragAndDrop } from "@/hooks/useDragAndDrop";
import { useSelectionHandlers } from "@/hooks/useSelectionHandlers";
import SelectionCheckbox from "./SelectionCheckbox";
import { useContentSelection } from "@/contexts/selection/SelectionContext";
import { isHighlightedText, getContentUrl, getHighlightedText } from "./contentCardUtils";

/**
 * Unified ContentCard component that handles both grid and list views
 * with consistent behavior across the application.
 */
const ContentCard = ({
  item,
  view = "grid",
  onClick,
  onEdit,
  onDelete,
  onArchive,
  onPin,
  onUnpin,
  onTagAdd,
  onTagRemove,
  allContentIds = [],
  selectable = false,
  children,
  // Custom click handlers for drag-aware components
  onThumbnailClick,
  onLabelClick,
}: ContentCardProps & { 
  children?: React.ReactNode;
  onThumbnailClick?: (e: React.MouseEvent) => void;
  onLabelClick?: (e: React.MouseEvent) => void;
}) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showTagsOverlay, setShowTagsOverlay] = useState(false);
  const [showTagSelectionDialog, setShowTagSelectionDialog] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [contentToDelete, setContentToDelete] = useState<string | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const { contentTags, addTag, removeTag } = useContentTags(item.id);
  const { handleContentDragStart, handleContentDragEnd } = useDragAndDrop();
  const { selectedIds } = useContentSelection();
  
  // Initialize selection handlers
  const { selected, handleSelectionClick } = useSelectionHandlers({
    contentId: item.id,
    allContentIds: allContentIds,
    onClick: onClick
  });
  
  // Detect if this is highlighted text
  const isHighlight = isHighlightedText(item);
  const highlightedText = getHighlightedText(item);
  
  // Skip rendering if view is network, freeform, or list (now handled directly in ContentList)
  if (view === "network" || view === "freeform" || view === "list") {
    return null;
  }
  
  useEffect(() => {
    const handleMouseLeave = () => {
      setShowTagsOverlay(false);
      setIsHovering(false);
    };

    const currentRef = cardRef.current;
    if (currentRef) {
      currentRef.addEventListener("mouseleave", handleMouseLeave);
    }
    
    return () => {
      if (currentRef) {
        currentRef.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, []);
  
  const handleCardClick = (e?: React.MouseEvent) => {
    console.log(`[ContentCard] handleCardClick called for item ${item.id}`, {
      hasOnClick: !!onClick,
      selectable,
      eventType: e?.type
    });
    
    if (selectable && e) {
      handleSelectionClick(e);
    } else if (onClick) {
      console.log(`[ContentCard] Calling onClick for item ${item.id}`);
      onClick();
    } else {
      console.warn(`[ContentCard] No onClick handler provided for item ${item.id}`);
    }
  };

  const handleThumbnailClick = (e: React.MouseEvent) => {
    // Use custom handler if provided (for drag-aware components)
    if (onThumbnailClick) {
      onThumbnailClick(e);
      return;
    }
    
    // Default behavior for non-drag-aware components
    e.preventDefault();
    e.stopPropagation();
    
    // Use deep link URL for highlighted text, regular URL otherwise
    const targetUrl = getContentUrl(item);
    console.log(`[NAVIGATION DEBUG] ContentCard - Thumbnail clicked for item ${item.id}, opening URL: ${targetUrl}`);
    window.open(targetUrl, "_blank", "noopener");
  };

  const handleLabelClick = (e: React.MouseEvent) => {
    // Use custom handler if provided (for drag-aware components)
    if (onLabelClick) {
      onLabelClick(e);
      return;
    }
    
    // Default behavior for non-drag-aware components
    e.stopPropagation();
    console.log(`[ContentCard] Title area clicked for item ${item.id}`);
    handleCardClick(e);
  };

  const handleDeleteRequest = (id: string) => {
    console.log(`[NAVIGATION DEBUG] ContentCard - Delete requested for item ${id}`);
    // Store the ID of the content to delete
    setContentToDelete(id);
    // Show the delete confirmation dialog
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    console.log(`[NAVIGATION DEBUG] ContentCard - Delete confirmed for item ${contentToDelete}`);
    
    if (contentToDelete && onDelete) {
      console.log(`[NAVIGATION DEBUG] ContentCard - Executing delete for item ${contentToDelete}`);
      onDelete(contentToDelete);
    }
  };

  const handleDeleteDialogOpenChange = (open: boolean) => {
    console.log(`[NAVIGATION DEBUG] ContentCard - Delete dialog open state changing to: ${open}`);
    setShowDeleteDialog(open);
  };

  const toggleTagsOverlay = (e: React.MouseEvent) => {
    console.log(`[NAVIGATION DEBUG] ContentCard - Tags overlay toggled for item ${item.id}`);
    e.stopPropagation(); // Prevent card click
    setShowTagsOverlay(!showTagsOverlay);
  };

  const handleRemoveTag = (e: React.MouseEvent, tagId: string) => {
    console.log(`[NAVIGATION DEBUG] ContentCard - Remove tag ${tagId} for item ${item.id}`);
    e.stopPropagation(); // Prevent card click
    removeTag(tagId);
  };

  const handleTagsOverlayClick = (e: React.MouseEvent) => {
    console.log(`[NAVIGATION DEBUG] ContentCard - Tags overlay clicked for item ${item.id}`);
    e.stopPropagation(); // Prevent any parent handlers from capturing the event
  };
  
  const handleAddTagClick = () => {
    console.log(`[NAVIGATION DEBUG] ContentCard - Add tag button clicked for item ${item.id}`);
    setShowTagSelectionDialog(true); // Open the tag selection dialog directly
  };

  const handleDragStart = (e: React.DragEvent) => {
    console.log(`[DRAG DEBUG] ContentCard - Drag started for item ${item.id}`);
    
    // If items are selected and this item is part of the selection, drag all selected items
    if (selected && selectedIds.length > 1) {
      const dragData = {
        contentIds: selectedIds,
        contentTitles: selectedIds.length > 1 ? `${selectedIds.length} items` : item.title,
        isMultiDrag: true
      };
      
      handleContentDragStart(e, dragData);
    } else {
      // Single item drag
      const dragData = {
        contentId: item.id,
        contentTitle: item.title,
        currentCollectionId: item.collection_id
      };
      
      handleContentDragStart(e, dragData);
    }
    
    // Set a visual indication for the drag operation
    if (e.dataTransfer.setDragImage) {
      const ghostElement = document.createElement('div');
      ghostElement.classList.add('bg-white', 'dark:bg-gray-800', 'p-2', 'rounded', 'shadow-md');
      
      // Show either single item or multiple items count
      if (selected && selectedIds.length > 1) {
        ghostElement.textContent = `Dragging ${selectedIds.length} items`;
      } else {
        ghostElement.textContent = item.title || 'Content';
      }
      
      ghostElement.style.width = '180px';
      ghostElement.style.opacity = '0.7';
      document.body.appendChild(ghostElement);
      e.dataTransfer.setDragImage(ghostElement, 90, 30);
      
      // Clean up the ghost element after a short delay
      setTimeout(() => {
        document.body.removeChild(ghostElement);
      }, 100);
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    handleContentDragEnd();
  };

  const cardColor = item.collection_color || "#6B7789";

  if (children) {
    return (
      <ContentCardUI 
        color={cardColor}
        onClick={undefined} // Don't pass onClick to UI wrapper when using children
        isClickable={false} // Let children handle their own clicks
        fullHeight={view === "grid"}
        withPadding={false}
      >
        {children}
      </ContentCardUI>
    );
  }

  return (
    <>
      <div 
        ref={cardRef} 
        className="relative"
        onMouseEnter={() => setIsHovering(true)}
        draggable={true}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        data-content-card="true"
      >
        <ContentCardUI 
          color={cardColor}
          onClick={undefined} // Don't pass onClick to UI wrapper - handle internally
          isClickable={false} // Let internal elements handle clicks
          fullHeight={view === "grid"}
          withPadding={false}
          className={selected ? "ring-2 ring-blue-500 ring-offset-2" : ""}
        >
          <div className="flex flex-col h-full">
            <div className="relative flex-1 group">
              {/* Selection checkbox - only shown when selectable is true */}
              {selectable && (isHovering || selected) && (
                <SelectionCheckbox 
                  isSelected={selected} 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectionClick(e);
                  }}
                  data-selection-checkbox="true"
                />
              )}
              
              {/* REMOVED: Highlighted text indicator - NO MORE BADGES */}
              
              {isHovering && !showTagsOverlay && (
                <button 
                  className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-gray-900/20 text-white hover:bg-gray-900/30 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleTagsOverlay(e);
                  }}
                >
                  <Tag size={18} />
                </button>
              )}
              
              <div 
                className="w-full rounded-t-lg overflow-hidden cursor-pointer"
                onClick={handleThumbnailClick}
              >
                <CardThumbnail 
                  thumbnailUrl={item.thumbnail_url} 
                  contentType={item.content_type}
                  title={item.title}
                  contentItem={item}
                />
              </div>
              
              {showTagsOverlay && (
                <div 
                  className="absolute inset-0 bg-gray-900/70 rounded-t-lg z-20 p-3 overflow-y-auto"
                  onClick={handleTagsOverlayClick}
                >
                  <div className="flex justify-between items-center text-white mb-2">
                    <span className="font-medium">TAGS</span>
                    <div onClick={(e) => e.stopPropagation()}>
                      <AddTagButton 
                        variant="circle"
                        showHoverText={false}
                        contentId={item.id}
                        forceUseExternalDialog={true}
                        onAddClick={handleAddTagClick}
                      />
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {contentTags.length > 0 ? (
                      contentTags.map(tag => (
                        <div 
                          key={tag.id} 
                          className="flex items-center rounded-full border-2 pl-3 pr-1 py-0.5 text-white text-sm"
                          style={{ borderColor: tag.color }}
                        >
                          <span className="mr-1">{tag.name}</span>
                          <button 
                            className="p-0.5 hover:bg-gray-700/50 rounded-full"
                            onClick={(e) => handleRemoveTag(e, tag.id)}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="text-white text-sm text-center w-full py-2">
                        No tags added yet
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="border-t border-border p-3 bg-card">
              <div className="flex justify-between items-start">
                <div 
                  className="flex-1 cursor-pointer min-h-[40px] flex flex-col justify-center" 
                  onClick={handleLabelClick}
                >
                  <h3 className="font-medium text-base line-clamp-1 leading-tight text-card-foreground">
                    {item.title}
                  </h3>
                  
                  {/* Show highlighted text preview for text-highlight content with same styling as regular description */}
                  {isHighlight && highlightedText ? (
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-1 leading-tight">
                      "{highlightedText}"
                    </p>
                  ) : item.description ? (
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-1 leading-tight">
                      {item.description}
                    </p>
                  ) : null}
                </div>
                
                <CardActionMenu
                  itemId={item.id}
                  itemTitle={item.title}
                  isArchived={item.archived}
                  isPinned={item.pinned || false}
                  onEdit={onEdit}
                  onArchive={onArchive}
                  onDelete={handleDeleteRequest}
                  onPin={onPin}
                  onUnpin={onUnpin}
                  variant="icon"
                  alignment="end"
                />
              </div>
            </div>
          </div>
        </ContentCardUI>
      </div>
      
      <GridCardDeleteDialog
        open={showDeleteDialog}
        onOpenChange={handleDeleteDialogOpenChange}
        itemTitle={item.title}
        onConfirm={handleConfirmDelete}
      />
      
      <AddTagButton 
        contentId={item.id}
        isDialogOpen={showTagSelectionDialog}
        onDialogOpenChange={(open) => {
          setShowTagSelectionDialog(open);
          
          if (!open && showTagSelectionDialog) {
            console.log(`[NAVIGATION DEBUG] ContentCard - Tag dialog closed, scheduling refresh`);
            setTimeout(() => {
              console.log(`[NAVIGATION DEBUG] ContentCard - Forcing page refresh after tag dialog close`);
              window.location.reload();
            }, 300);
          }
        }}
        className="hidden"
      />
    </>
  );
};

export default ContentCard;
